import { useSyncExternalStore } from 'react';
import type { ModuleStudioRepository } from './ModuleStudioRepository';

export interface StudioMutation { message: string; version: number; }
let revision = 0;
const listeners = new Set<() => void>();
const feedback = new Set<(event: StudioMutation) => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const subscribeStudioFeedback = (listener: (event: StudioMutation) => void) => {
  feedback.add(listener); return () => { feedback.delete(listener); };
};
export const useStudioRevision = () => useSyncExternalStore(subscribe, () => revision, () => 0);
export function invalidateStudioState(): void {
  revision += 1;
  listeners.forEach(listener => listener());
}

const messages: Partial<Record<keyof ModuleStudioRepository, string>> = {
  proposeModuleSpecVersion: 'proposed', proposeModuleSpecDraft: 'proposed', repairModuleSpecVersion: 'repaired',
  applyPrototypeAnalysis: 'applied', rejectPrototypeAnalysis: 'analysisRejected',
  createGenerationRun: 'delivery', depositGeneratorRun: 'deposited',
  deletePrototypeImport: 'retired', hidePrototypeImport: 'hidden', restorePrototypeImport: 'restored',
};
/** Après résolution seulement, et après invalidation du cache : aucune réussite optimiste. */
export function withStudioMutationFeedback(repository: ModuleStudioRepository): ModuleStudioRepository {
  const wrapped = { ...repository };
  const target = wrapped as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
  const source = repository as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
  for (const method of Object.keys(repository) as (keyof ModuleStudioRepository)[]) {
    if (!messages[method] && method !== 'decideModuleSpecVersion') continue;
    target[method] = async (...args) => {
      const result = await source[method](...args);
      const input = args[2] as { decision?: string } | undefined;
      const message = method === 'decideModuleSpecVersion' ? (input?.decision === 'accept' ? 'accepted' : 'rejected') : messages[method]!;
      const value = result as { version?: number; status?: string } | null;
      // Une livraison peut revenir bloquée/échouée : le toast confirme la préparation, jamais la mise en service.
      if (method !== 'createGenerationRun' || value?.status === 'succeeded') {
        feedback.forEach(listener => listener({ message, version: value?.version ?? 0 }));
      }
      revision += 1;
      listeners.forEach(listener => listener());
      return result;
    };
  }
  return wrapped;
}
